/**
 * feat-241 — naming a locked cell's blockers (P11, design R3).
 *
 * `blocking_track_ids` are IDS, not sentences. The web names those tracks from
 * the `tracks` titles it ALREADY renders as column headers.
 *
 * It deliberately does NOT read `lock_reason.text`: slice 3's rule that the
 * client renders that verbatim is about a BILINGUAL PHONE. `lock_reason.text` is
 * composed in the tenant's CONTENT language, so reusing it here would put
 * Spanish lock copy in an English manager UI. Naming a column the manager can
 * already see is not composing lock copy — and it makes "a lock reason can never
 * name a track absent from the matrix" STRUCTURAL rather than a promise.
 */
import type { CoverageTrack } from '../../../services/curriculumCoverageService';

/**
 * Blocker titles, ordered by the response's own `tracks` order — i.e. left to
 * right across the matrix, matching reading order.
 *
 * NOT sorted by title, and NOT in the array's own order: the backend's order for
 * `blocking_track_ids` is unspecified, so relying on it would make which name a
 * manager sees depend on a query plan.
 *
 * An id with no matching track is DROPPED rather than rendered as a blank. The
 * backend makes that structurally impossible (AC8); if it happens anyway the
 * caller renders a bare "Locked" rather than a dangling "Needs ".
 */
export function resolveBlockerTitles(
  blockingTrackIds: readonly string[],
  tracks: readonly CoverageTrack[],
  /** feat-302 — tracks off-curriculum for this crew member. They never block
   *  (ADR 0001 §4), so they are never named as a blocker either. */
  notApplicable: ReadonlySet<string> = new Set(),
): string[] {
  if (blockingTrackIds.length === 0) return [];
  const blocking = new Set(blockingTrackIds);
  return tracks.filter((t) => blocking.has(t.id) && !notApplicable.has(t.id)).map((t) => t.title);
}
